import { useState } from "react";
import Editor from "react-simple-code-editor";

import { highlight, languages } from "@/lib/prism";
import type { ProjectCodeEval } from "@/reducers/environments";

const TEXTAREA_CLASS =
  "mt-1 min-h-[120px] w-full resize-y rounded border bg-surface-sunken p-3 font-mono text-xs text-textStrong placeholder:text-iconMinimal focus:border-interactive-selected focus:outline-none";

type AdvancedSettingsContentProps = {
  codeEval?: ProjectCodeEval;
  onChange?: (codeEval: ProjectCodeEval) => void;
};

export const AdvancedSettingsContent = ({
  codeEval,
  onChange,
}: AdvancedSettingsContentProps) => {
  const [values, setValues] = useState<ProjectCodeEval>({
    onPageLoad: codeEval?.onPageLoad ?? "",
    onPageChange: codeEval?.onPageChange ?? "",
    onIdentify: codeEval?.onIdentify ?? "",
    onTrack: codeEval?.onTrack ?? "",
  });

  const update = (key: keyof ProjectCodeEval) => (value: string) => {
    const next = { ...values, [key]: value };
    setValues(next);
    onChange?.(next);
  };

  const hardCodedProps = {
    onIdentify: {
      userID: "The unique identifier of the user.",
      userProps: "User properties passed to the identify call.",
    },
    onTrack: {
      eventName: "The name of the event.",
      eventProperties: "Event properties passed to the track call.",
      builtinProperties: "Properties Freshpaint computes for each event.",
      eventOptions: "Event options passed to manual track calls.",
    },
  };

  const formatHardCodedProps = (key: keyof typeof hardCodedProps) => {
    return (
      `// These variables are available in your code:\n\n` +
      Object.entries(hardCodedProps[key])
        .map(([varName, description]) => `var ${varName}; // ${description}`)
        .join("\n")
    );
  };

  return (
    <>
      <div>
        <h3 className="text-sm font-semibold">Tag Manager</h3>
        <p className="text-textSubtle text-sm">
          Our tag manager feature allows you to inject code directly on your
          website.
        </p>
      </div>

      <div>
        <label className="text-textStrong text-sm font-medium">
          On Initial Document Load:
        </label>
        <Editor
          className={TEXTAREA_CLASS}
          value={values.onPageLoad ?? ""}
          highlight={(code) => highlight(code, languages.js, "JavaScript")}
          onValueChange={update("onPageLoad")}
          placeholder="Javascript code..."
          spellCheck={false}
        />
      </div>

      <div>
        <label className="text-textStrong text-sm font-medium">
          On Page Change:
        </label>
        <Editor
          className={TEXTAREA_CLASS}
          value={values.onPageChange ?? ""}
          highlight={(code) => highlight(code, languages.js, "JavaScript")}
          onValueChange={update("onPageChange")}
          placeholder="Javascript code..."
          spellCheck={false}
        />
      </div>

      <div>
        <label className="text-textStrong text-sm font-medium">
          On Identify:
        </label>
        <pre className="bg-surface-sunken text-textSecondary mt-1 overflow-auto rounded border p-3 font-mono text-xs">
          <code>{formatHardCodedProps("onIdentify")}</code>
        </pre>
        <Editor
          className={TEXTAREA_CLASS}
          value={values.onIdentify ?? ""}
          highlight={(code) => highlight(code, languages.js, "JavaScript")}
          onValueChange={update("onIdentify")}
          placeholder="Javascript code..."
          spellCheck={false}
        />
      </div>

      <div>
        <label className="text-textStrong text-sm font-medium">On Track:</label>
        <pre className="bg-surface-sunken text-textSecondary mt-1 overflow-auto rounded border p-3 font-mono text-xs">
          <code>{formatHardCodedProps("onTrack")}</code>
        </pre>
        <Editor
          className={TEXTAREA_CLASS}
          value={values.onTrack ?? ""}
          highlight={(code) => highlight(code, languages.js, "JavaScript")}
          onValueChange={update("onTrack")}
          placeholder="Javascript code..."
          spellCheck={false}
        />
      </div>
    </>
  );
};
